"use client";

import { useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { debug } from "@/lib/utils/debug";

export interface AuthGuardDecisionInput {
  requireEmployee: boolean;
  requireAdmin: boolean;
  requireClient: boolean;
  requiredUserTypes: string[];
  redirectTo?: string;
  onUnauthorized?: () => void;
}

export interface AuthGuardDecision {
  effectivelyLoading: boolean;
  isAuthenticated: boolean;
  hasAccess: boolean;
  redirectDestination: string;
}

/**
 * Encapsulates AuthGuard's auth-state read, access decision, and redirect side-effect.
 * Returns the values needed to render loading / login / access-denied / children states.
 */
export function useAuthGuardDecision({
  requireEmployee,
  requireAdmin,
  requireClient,
  requiredUserTypes,
  redirectTo,
  onUnauthorized,
}: AuthGuardDecisionInput): AuthGuardDecision {
  const { isAuthenticated, appUser, isLoading, isEmployee, isAdmin, isClientUser, isInitialized } =
    useUniversalAuth();
  const router = useRouter();

  // For role-gated guards, wait for `appUser` so we don't flash "Access Denied" on refresh.
  const waitingForRole =
    (requireAdmin || requireEmployee || requireClient) && isAuthenticated && appUser === null;
  const effectivelyLoading = !isInitialized || isLoading || waitingForRole;

  const getRedirectDestination = useCallback(() => {
    if (redirectTo) return redirectTo;
    if (requireClient) return "/auth/client";
    if (requireAdmin) return "/access-denied";
    if (requireEmployee) return "/auth/employee";
    return "/";
  }, [redirectTo, requireClient, requireAdmin, requireEmployee]);

  const hasRequiredAccess = useCallback((): boolean => {
    if (requireAdmin && !isAdmin) return false;
    if (requireEmployee && !isEmployee) return false;
    if (requireClient && !isClientUser) return false;
    if (
      requiredUserTypes.length > 0 &&
      appUser?.role &&
      !requiredUserTypes.includes(appUser.role)
    ) {
      return false;
    }
    return true;
  }, [
    requireAdmin,
    requireEmployee,
    requireClient,
    isAdmin,
    isEmployee,
    isClientUser,
    requiredUserTypes,
    appUser?.role,
  ]);

  useEffect(() => {
    if (effectivelyLoading) return;
    const redirectDestination = getRedirectDestination();

    if (!isAuthenticated) {
      debug.redirect("AuthGuard: User not authenticated, redirecting to", redirectDestination);
      onUnauthorized?.();
      router.push(redirectDestination);
      return;
    }

    if (!hasRequiredAccess()) {
      debug.redirect("AuthGuard: User access denied, redirecting to", redirectDestination);
      onUnauthorized?.();
      router.push(redirectDestination);
    }
  }, [
    isAuthenticated,
    appUser?.role,
    appUser,
    effectivelyLoading,
    requireEmployee,
    requireAdmin,
    requireClient,
    requiredUserTypes,
    redirectTo,
    router,
    onUnauthorized,
    isEmployee,
    isAdmin,
    isClientUser,
    getRedirectDestination,
    hasRequiredAccess,
  ]);

  return {
    effectivelyLoading,
    isAuthenticated,
    hasAccess: hasRequiredAccess(),
    redirectDestination: getRedirectDestination(),
  };
}
